import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { AuthCardShell, AuthInput, AuthSelect } from "./auth-card-shell";

export function SignUpCard({
    logoSrc,
    onSubmit,
    isSubmitting = false,
    firstName,
    onFirstNameChange,
    lastName,
    onLastNameChange,
    email,
    onEmailChange,
    password,
    onPasswordChange,
    confirmPassword,
    onConfirmPasswordChange,
    phone,
    onPhoneChange,
    countryCode,
    onCountryChange,
    countryNameArray = [],
    stateCode,
    onStateChange,
    stateNameArray = [],
    cityCode,
    onCityChange,
    cityNameArray = [],
    currentProfileImage,
    onProfileUploadClick,
    profileImageInputRef,
    onProfileImageChange,
    isUploadingImage = false,
    loginTo = "/",
}) {
    return (
        <AuthCardShell
            embedded
            logoSrc={logoSrc}
            title="Create an Account"
            description="Please create an account to continue."
            maxWidthClass="max-w-lg"
            onSubmit={onSubmit}
            formClassName="sign-up-card-form"
        >
            <div className="sign-up-card-fields space-y-3">
                <motion.div className="grid grid-cols-1 gap-3 sm:grid-cols-2" whileHover={{ scale: 1.005 }}>
                    <AuthInput
                        type="text"
                        id="fname"
                        name="fname"
                        placeholder="Enter First name"
                        value={firstName}
                        onChange={(e) => onFirstNameChange?.(e.target.value)}
                        autoComplete="given-name"
                        required
                    />
                    <AuthInput
                        type="text"
                        id="lname"
                        name="lname"
                        placeholder="Enter Last name"
                        value={lastName}
                        onChange={(e) => onLastNameChange?.(e.target.value)}
                        autoComplete="family-name"
                        required
                    />
                </motion.div>

                <AuthInput
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter email"
                    value={email}
                    onChange={(e) => onEmailChange?.(e.target.value)}
                    autoComplete="email"
                    required
                />

                <AuthInput
                    type="password"
                    id="password"
                    name="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => onPasswordChange?.(e.target.value)}
                    autoComplete="new-password"
                    required
                />

                <AuthInput
                    type="password"
                    id="confirm_password"
                    name="confirm_password"
                    placeholder="Confirm password"
                    value={confirmPassword}
                    onChange={(e) => onConfirmPasswordChange?.(e.target.value)}
                    autoComplete="new-password"
                    required
                />

                <motion.div className="grid grid-cols-1 gap-3 sm:grid-cols-2" whileHover={{ scale: 1.005 }}>
                    <AuthInput
                        type="text"
                        id="phone"
                        name="phone"
                        placeholder="Enter Phone number"
                        value={phone}
                        onChange={(e) => onPhoneChange?.(e.target.value)}
                        autoComplete="tel"
                        required
                    />
                    <AuthSelect
                        name="edit-countrys"
                        id="edit-countrys"
                        value={countryCode}
                        onChange={onCountryChange}
                    >
                        <option value="">Select Country</option>
                        {countryNameArray.map((country) => (
                            <option key={country.code} value={country.code}>
                                {country.name}
                            </option>
                        ))}
                    </AuthSelect>
                </motion.div>

                <motion.div className="grid grid-cols-1 gap-3 sm:grid-cols-2" whileHover={{ scale: 1.005 }}>
                    <AuthSelect
                        name="edit-states"
                        id="edit-states"
                        value={stateCode}
                        onChange={onStateChange}
                    >
                        <option value="">Select State</option>
                        {stateNameArray.map((states) => (
                            <option key={states.region_code} value={states.region_code}>
                                {states.region_name}
                            </option>
                        ))}
                    </AuthSelect>
                    <AuthSelect name="edit-citys" id="edit-citys" value={cityCode} onChange={onCityChange}>
                        <option value="">Select City</option>
                        {cityNameArray.map((citys) => (
                            <option key={citys.city_id} value={citys.city_id}>
                                {citys.city_name}
                            </option>
                        ))}
                    </AuthSelect>
                </motion.div>

                <div className="flex flex-wrap items-center gap-3 pt-1">
                    <button
                        type="button"
                        className="sign-up-profile-upload-btn"
                        id="uploadProfileImageBtn"
                        aria-label="Upload Profile Image"
                        onClick={onProfileUploadClick}
                        disabled={isUploadingImage}
                    >
                        <img
                            className="profile-upload-image"
                            src={currentProfileImage}
                            alt="User avatar"
                            key={currentProfileImage}
                        />
                        Upload profile image
                    </button>
                    <input
                        type="file"
                        accept="image/*"
                        ref={profileImageInputRef}
                        className="hidden"
                        onChange={onProfileImageChange}
                    />
                </div>

                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    type="submit"
                    disabled={isSubmitting || isUploadingImage}
                    className="group/button relative mt-2 w-full"
                >
                    <div className="relative flex h-10 items-center justify-center overflow-hidden rounded-lg bg-white font-medium text-black transition-all">
                        {isSubmitting ? (
                            <div className="h-4 w-4 animate-spin rounded-full border-2 border-black/70 border-t-transparent" />
                        ) : (
                            <span className="flex items-center gap-1 text-sm font-medium">
                                Create your account
                                <ArrowRight className="h-3 w-3 transition-transform group-hover/button:translate-x-1" />
                            </span>
                        )}
                    </div>
                </motion.button>

                <motion.p
                    className="pt-2 text-center text-xs text-white/60"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.35 }}
                >
                    Already have an account?{" "}
                    <Link to={loginTo} className="group/login relative inline-block font-medium text-white">
                        <span className="transition-colors group-hover/login:text-white/70">Login</span>
                        <span className="absolute bottom-0 left-0 h-px w-0 bg-white transition-all group-hover/login:w-full" />
                    </Link>
                </motion.p>
            </div>
        </AuthCardShell>
    );
}
